import { InvalidUploadError } from '#storage/keys'

/**
 * Resized copies of CMS media, and the one place their names are composed.
 *
 * **The widths are a closed list.** An open `w` would let anyone mint a new cache entry, a new
 * Lambda invocation and a new stored object per request by counting upwards, and the bill for
 * that lands on the tenant whose image it was.
 */
export const MEDIA_WIDTHS = [320, 640, 960, 1280, 1920, 2560] as const
export type MediaWidth = (typeof MEDIA_WIDTHS)[number]

/** What the edge negotiates `Accept` down to. Quality is fixed per format, never a parameter. */
export const MEDIA_FORMATS = ['avif', 'webp', 'jpeg'] as const
export type MediaFormat = (typeof MEDIA_FORMATS)[number]

/**
 * The crops the site asks for — exactly the ratios its sections render, and no others.
 *
 * Closed for the same reason as the widths, and multiplied by them: every ratio is six more
 * variants per format. Written `16x9` rather than `16:9` because the token travels in a query
 * string and an object key, where a colon is encoded in one and awkward in the other.
 */
export const MEDIA_RATIOS = ['16x9', '4x3', '3x2', '1x1', '3x4'] as const
export type MediaRatio = (typeof MEDIA_RATIOS)[number]

export function isMediaRatio(value: string): value is MediaRatio {
  return (MEDIA_RATIOS as readonly string[]).includes(value)
}

/** `16x9` → `16 / 9`, for CSS `aspect-ratio`. */
export function ratioCss(ratio: MediaRatio): string {
  const [w, h] = ratio.split('x')
  return `${w} / ${h}`
}

/** `16x9` → 1.777…, width over height. */
export function ratioValue(ratio: MediaRatio): number {
  const [w, h] = ratio.split('x').map(Number)
  return (w ?? 1) / (h ?? 1)
}

/** Basis points, 0–10000 on each axis, as `cms_media` stores them. */
export type FocalPoint = { focalX: number; focalY: number }

/**
 * The crops an editor is shown, named the way the site's sections use them.
 *
 * Nothing is generated from this list: each preset is only a ratio and a width that `/_img/`
 * already serves on demand, so a preset can never name a variant the pipeline would refuse. The
 * widths are the nearest the closed list allows — a card at 768 would be a seventh width, and a
 * seventh width is a new variant per image, per ratio, per format.
 */
export const MEDIA_PRESETS = [
  { name: 'hero', label: 'Hero', ratio: '16x9', width: 1920 },
  { name: 'card', label: 'Card', ratio: '4x3', width: 640 },
  { name: 'wide', label: 'Wide', ratio: '3x2', width: 960 },
  { name: 'avatar', label: 'Avatar', ratio: '1x1', width: 320 },
  { name: 'portrait', label: 'Portrait', ratio: '3x4', width: 640 },
] as const satisfies readonly {
  name: string
  label: string
  ratio: MediaRatio
  width: MediaWidth
}[]
export type MediaPreset = (typeof MEDIA_PRESETS)[number]
export type MediaPresetName = MediaPreset['name']

/** A preset's output size: its width, and the height its ratio gives it. */
export function presetSize(preset: MediaPreset): { width: number; height: number } {
  return { width: preset.width, height: Math.round(preset.width / ratioValue(preset.ratio)) }
}

export type Region = { left: number; top: number; width: number; height: number }

/**
 * The largest region of `ratio` that fits the image, centered on the focal point as far as the
 * edges allow.
 *
 * Largest, so a crop only ever removes what the ratio forces it to. Clamped rather than centered
 * exactly, so a point in a corner pulls the region into that corner instead of pushing it off the
 * image — the point is always inside the result, which is the one promise a focal point makes.
 *
 * Here rather than beside the transform so the console's preview draws the region the Lambda cuts,
 * from the same arithmetic.
 */
export function cropRegion(
  image: { width: number; height: number },
  ratio: MediaRatio,
  focal: FocalPoint,
): Region {
  const target = ratioValue(ratio)
  const wide = image.width / image.height > target
  const width = wide ? Math.max(1, Math.round(image.height * target)) : image.width
  const height = wide ? image.height : Math.max(1, Math.round(image.width / target))

  const centerX = (focal.focalX / 10_000) * image.width
  const centerY = (focal.focalY / 10_000) * image.height

  return {
    left: clamp(Math.round(centerX - width / 2), 0, image.width - width),
    top: clamp(Math.round(centerY - height / 2), 0, image.height - height),
    width,
    height,
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export type MediaCrop = { x: number; y: number; width: number; height: number }

type Dimensions = { width: number; height: number }

export function cropFits(crop: MediaCrop, image: Dimensions): boolean {
  return (
    [crop.x, crop.y, crop.width, crop.height].every(Number.isInteger) &&
    crop.x >= 0 &&
    crop.y >= 0 &&
    crop.width >= 1 &&
    crop.height >= 1 &&
    crop.x + crop.width <= image.width &&
    crop.y + crop.height <= image.height
  )
}

export function clampCrop(crop: MediaCrop, image: Dimensions): MediaCrop | null {
  const x = clamp(crop.x, 0, image.width - 1)
  const y = clamp(crop.y, 0, image.height - 1)
  const width = clamp(crop.width, 1, image.width - x)
  const height = clamp(crop.height, 1, image.height - y)
  return image.width < 1 || image.height < 1 ? null : { x, y, width, height }
}

export function cropToken(crop: MediaCrop): string {
  return `${crop.x}x${crop.y}x${crop.width}x${crop.height}`
}

const CROP_TOKEN = /^(0|[1-9]\d{0,5})x(0|[1-9]\d{0,5})x([1-9]\d{0,5})x([1-9]\d{0,5})$/

export const CROP_TOKEN_PATTERN = CROP_TOKEN.source

export function parseCropToken(value: string): MediaCrop | null {
  const match = CROP_TOKEN.exec(value)
  if (match === null) return null
  const [x, y, width, height] = match.slice(1).map(Number)
  if (x === undefined || y === undefined || width === undefined || height === undefined) return null
  return width < 1 || height < 1 ? null : { x, y, width, height }
}

export function sameCrop(a: MediaCrop | null, b: MediaCrop | null): boolean {
  if (a === null || b === null) return a === b
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
}

export function clampFocalToCrop(
  image: Dimensions,
  crop: MediaCrop | null,
  focal: FocalPoint,
): FocalPoint {
  if (crop === null || image.width < 1 || image.height < 1) return focal
  const x = clamp((focal.focalX / 10_000) * image.width, crop.x, crop.x + crop.width)
  const y = clamp((focal.focalY / 10_000) * image.height, crop.y, crop.y + crop.height)
  return {
    focalX: clamp(Math.round((x / image.width) * 10_000), 0, 10_000),
    focalY: clamp(Math.round((y / image.height) * 10_000), 0, 10_000),
  }
}

function focalWithin(image: Dimensions, crop: MediaCrop, focal: FocalPoint): FocalPoint {
  const x = clamp((focal.focalX / 10_000) * image.width - crop.x, 0, crop.width)
  const y = clamp((focal.focalY / 10_000) * image.height - crop.y, 0, crop.height)
  return {
    focalX: Math.round((x / crop.width) * 10_000),
    focalY: Math.round((y / crop.height) * 10_000),
  }
}

export function renderRegion(
  image: Dimensions,
  crop: MediaCrop | null,
  ratio: MediaRatio | null,
  focal: FocalPoint,
): Region {
  const base = crop ?? { x: 0, y: 0, width: image.width, height: image.height }
  if (ratio === null) return { left: base.x, top: base.y, width: base.width, height: base.height }
  const local = cropRegion(
    { width: base.width, height: base.height },
    ratio,
    focalWithin(image, base, focal),
  )
  return {
    left: base.x + local.left,
    top: base.y + local.top,
    width: local.width,
    height: local.height,
  }
}

export function isMediaWidth(value: number): value is MediaWidth {
  return (MEDIA_WIDTHS as readonly number[]).includes(value)
}

export const TRANSFORMABLE_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/avif',
])

export const PASSTHROUGH_TYPES: ReadonlySet<string> = new Set(['image/gif'])

export function negotiateFormat(requested: string | null, accept: string | null): MediaFormat {
  const format = (requested ?? '').trim().toLowerCase()
  if (isMediaFormat(format)) return format
  const header = accept ?? ''
  return header.includes('image/avif') ? 'avif' : header.includes('image/webp') ? 'webp' : 'jpeg'
}

export function isMediaFormat(value: string): value is MediaFormat {
  return (MEDIA_FORMATS as readonly string[]).includes(value)
}

/**
 * `tenants/{id}/cms-media/{objectId}/{filename}` → `…/{filename}/w640.webp`, or with a crop
 * `…/{filename}/w640-16x9-f2500x7000.webp`.
 *
 * The variant lives **under** the original's key, so it keeps the tenant prefix: a lifecycle
 * rule, an audit or a cleanup scoped to one tenant reaches its variants without a second
 * naming scheme to remember.
 *
 * **A crop names its ratio and its focal point.** The ratio because a 16:9 and a 1:1 at the same
 * width are different images. The focal point because it can change after the variant exists:
 * with it in the name, moving the point makes the old crop unreachable instead of stale, and the
 * next request builds the new one. The uncropped name is unchanged, so every variant produced
 * before crops existed is still found.
 */
export function variantKey(
  key: string,
  width: number,
  format: string,
  crop?: { ratio: string } & FocalPoint,
  area?: MediaCrop | null,
): string {
  if (key === '' || key.startsWith('/') || key.includes('..') || key.includes('\\')) {
    throw new InvalidUploadError('not an object key')
  }
  if (!isMediaWidth(width)) {
    throw new InvalidUploadError(`width ${width} is not one of ${MEDIA_WIDTHS.join(', ')}`)
  }
  if (!isMediaFormat(format)) {
    throw new InvalidUploadError(`format ${format} is not one of ${MEDIA_FORMATS.join(', ')}`)
  }
  const cut = area === undefined || area === null ? '' : `-c${cropToken(area)}`
  if (crop === undefined) return `${key}/w${width}${cut}.${format}`

  if (!isMediaRatio(crop.ratio)) {
    throw new InvalidUploadError(`ratio ${crop.ratio} is not one of ${MEDIA_RATIOS.join(', ')}`)
  }
  if (!isBasisPoint(crop.focalX) || !isBasisPoint(crop.focalY)) {
    throw new InvalidUploadError('focal point is outside 0–10000')
  }
  return `${key}/w${width}${cut}-${crop.ratio}-f${crop.focalX}x${crop.focalY}.${format}`
}

function isBasisPoint(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 10_000
}
